import { Button, Popconfirm, Result } from "antd";
import { MdOutlineFileUpload, MdUploadFile } from "react-icons/md";
import { useParams } from "react-router-dom";
import { Dropzone } from "../../components/Dropzone";
import { HelpButton } from "../../components/HelpButton";
import { Instructions } from "../../components/Instructions";
import { IntroductionHeader } from "../../components/IntroductionHeader";
import { Layout } from "../../components/Layout";
import Spinner from "../../components/Loading/Spinner";
import { useCustomerSpend } from "./useCustomerSpend";
import {
  CustomerSpendUploadStatus,
  useCustomerSpendUpload,
} from "./useCustomerSpendUpload";

const MAX_FILES = 20;
const MAX_UPLOAD_SIZE = 1024 * 1024 * 10;
const ALLOWED_EXTENSIONS = ["pdf", "csv", "xls", "xlsx"];

const Footer = () => {
  const { isLoading, files, submit } = useCustomerSpendUpload();

  return (
    <div className="flex justify-between items-center mt-6">
      <Popconfirm
        title="This action will close this window and discard any unsaved changes. Are you sure you want to continue?"
        okText="Yes. Close window"
        cancelText="No"
        overlayClassName="max-w-sm"
        onConfirm={window.close}
      >
        <button className="font-medium text-gray-700 transition-colors hover:text-gray-900">
          Cancel
        </button>
      </Popconfirm>
      <Button
        disabled={isLoading || files.length < 1}
        loading={isLoading}
        className="flex gap-2 items-center px-6 py-2 font-semibold text-white bg-gray-800 rounded-full transition-all hover:bg-gray-900 hover:shadow-none hover:border-gray-950 hover:text-white"
        onClick={() => submit()}
      >
        Upload
        <MdOutlineFileUpload className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const UploadCustomerSpend = () => {
  const { id } = useParams<{ id: string }>();
  const { isLoading, data, error } = useCustomerSpend(id);
  const { status, files, setFiles, retry } = useCustomerSpendUpload();

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Layout>
        <Result status="error" title="Error" subTitle="Something went wrong" />
      </Layout>
    );
  }

  return (
    <Layout>
      <IntroductionHeader
        title={
          status === CustomerSpendUploadStatus.Success
            ? "You're all set!"
            : "Upload your supplier data"
        }
        description={
          status === CustomerSpendUploadStatus.Success
            ? "Your supplier data has been uploaded successfully."
            : "Export and share your vendor spend file"
        }
        success={status === CustomerSpendUploadStatus.Success}
        sourceLogo={data?.organization?.linkLogoUrl ?? undefined}
      />
      <Instructions
        title="Upload your supplier data"
        icon={MdUploadFile}
        steps={[
          "View your vendor spend in your ERP",
          "Export the data in a tabular format",
          "Return here and upload the resulting files in the box below",
        ]}
        hidden={status !== CustomerSpendUploadStatus.Idle}
      >
        {[
          CustomerSpendUploadStatus.Idle,
          CustomerSpendUploadStatus.Failure,
        ].includes(status) && <HelpButton url="mailto:support@rutter.com" />}

        <Dropzone
          files={files}
          onChange={setFiles}
          maxFiles={MAX_FILES}
          maxSizePerFile={MAX_UPLOAD_SIZE}
          allowedExtensions={ALLOWED_EXTENSIONS}
          isUploading={status === CustomerSpendUploadStatus.Uploading}
          disabled={status === CustomerSpendUploadStatus.Uploading}
          readonly={[
            CustomerSpendUploadStatus.Uploading,
            CustomerSpendUploadStatus.Success,
          ].includes(status)}
          retryable
          onRetry={retry}
        />
      </Instructions>
      {status !== CustomerSpendUploadStatus.Success && <Footer />}
    </Layout>
  );
};
